import { call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import * as agentsApi from '../api/agents-api';
import { Agent } from '../types';
import {
  createAgentRequest,
  updateAgentRequest,
  getAgentsRequest,
  getAgentByIdRequest,
  deleteAgentRequest,
  updateAgentStatusRequest,
  createAgentSuccess,
  updateAgentSuccess,
  getAgentsSuccess,
  getAgentByIdSuccess,
  deleteAgentSuccess,
  updateAgentStatusSuccess,
  agentFailure,
  CreateAgentRequest,
  UpdateAgentRequest,
  GetAgentsRequest,
  GetAgentByIdRequest,
  DeleteAgentRequest,
  UpdateAgentStatusRequest,
} from './agent-slice';

// Mapper para transformar la respuesta de la API al tipo Agent del frontend
function mapApiAgentToAgent(apiAgent: any): Agent {
  return {
    id: apiAgent.id,
    firstName: apiAgent.firstName || apiAgent.name?.split(' ')[0] || '',
    lastName: apiAgent.lastName || apiAgent.name?.split(' ').slice(1).join(' ') || '',
    fullName: apiAgent.fullName || `${apiAgent.firstName || ''} ${apiAgent.lastName || ''}`.trim() || apiAgent.name || 'Sin nombre',
    email: apiAgent.email || '',
    phone: apiAgent.phone || apiAgent.phoneNumber || '',
    mobile: apiAgent.mobile || apiAgent.mobilePhone || '',
    status: apiAgent.status || 'inactive',
    agentType: apiAgent.agentType || apiAgent.role || apiAgent.type || '',
    // Propiedades de rendimiento
    propertiesAssigned: apiAgent.propertiesAssigned || 0,
    totalPropertiesSold: apiAgent.totalPropertiesSold || 0,
    totalSales: apiAgent.totalSales || 0,
    totalCommissions: apiAgent.totalCommissions || 0,
    commissionRate: apiAgent.commissionRate || 0,
    // Timestamps
    createdAt: apiAgent.createdAt || new Date().toISOString(),
    updatedAt: apiAgent.updatedAt || new Date().toISOString(),
  };
}

// Saga para crear agente
function* createAgentSaga(
  action: PayloadAction<CreateAgentRequest>
): Generator<any, void, any> {
  try {
    const { agentData } = action.payload;


    const response: any = yield call(agentsApi.createAgent, agentData);


    const apiData = response.data || response;
    const mappedAgent = mapApiAgentToAgent(apiData);

    yield put(createAgentSuccess(mappedAgent));

  } catch (error: any) {
    yield put(
      agentFailure(
        error.message || error.response?.data?.message || 'Error al crear el agente'
      )
    );
  }
}

// Saga para actualizar agente
function* updateAgentSaga(
  action: PayloadAction<UpdateAgentRequest>
): Generator<any, void, any> {
  try {
    const { id, agentData } = action.payload;


    const response: any = yield call(agentsApi.updateAgent, id, agentData);


    const apiData = response.data || response;
    const mappedAgent = mapApiAgentToAgent(apiData);

    yield put(updateAgentSuccess(mappedAgent));

  } catch (error: any) {
    yield put(
      agentFailure(
        error.message || error.response?.data?.message || 'Error al actualizar el agente'
      )
    );
  }
}

// Saga para obtener agentes
function* getAgentsSaga(
  action: PayloadAction<GetAgentsRequest>
): Generator<any, void, any> {
  try {
    const { filters } = action.payload;


    const response: any = yield call(agentsApi.getAgents);


    // El backend puede devolver los datos en diferentes estructuras
    let agentsData = response.data?.agents || response.agents || response.data || response || [];

    // Asegurar que sea un array
    if (!Array.isArray(agentsData)) {
      agentsData = [];
    }

    // Mapear todos los agentes al tipo frontend
    const mappedAgents = agentsData.map((agent: any) => mapApiAgentToAgent(agent));


    yield put(getAgentsSuccess({
      agents: mappedAgents,
      pagination: response.data?.pagination || response.pagination,
    }));

  } catch (error: any) {
    yield put(
      agentFailure(
        error.message || error.response?.data?.message || 'Error al obtener los agentes'
      )
    );
  }
}

// Saga para obtener agente por ID
function* getAgentByIdSaga(
  action: PayloadAction<GetAgentByIdRequest>
): Generator<any, void, any> {
  try {
    const { id } = action.payload;


    const response: any = yield call(agentsApi.getAgentById, id);


    const apiData = response.data || response;
    const mappedAgent = mapApiAgentToAgent(apiData);

    yield put(getAgentByIdSuccess(mappedAgent));

  } catch (error: any) {
    yield put(
      agentFailure(
        error.message || error.response?.data?.message || 'Error al obtener el agente'
      )
    );
  }
}

// Saga para eliminar agente
function* deleteAgentSaga(
  action: PayloadAction<DeleteAgentRequest>
): Generator<any, void, any> {
  try {
    const { id } = action.payload;


    yield call(agentsApi.deleteAgent, id);


    yield put(deleteAgentSuccess(id));

  } catch (error: any) {
    yield put(
      agentFailure(
        error.message || error.response?.data?.message || 'Error al eliminar el agente'
      )
    );
  }
}

// Saga para actualizar estado del agente
function* updateAgentStatusSaga(
  action: PayloadAction<UpdateAgentStatusRequest>
): Generator<any, void, any> {
  try {
    const { id, status } = action.payload;


    const response: any = yield call(agentsApi.updateAgent as any, id, { status });


    const apiData = response.data || response;
    const mappedAgent = mapApiAgentToAgent(apiData);

    yield put(updateAgentStatusSuccess(mappedAgent));

  } catch (error: any) {
    yield put(
      agentFailure(
        error.message || error.response?.data?.message || 'Error al actualizar el estado del agente'
      )
    );
  }
}

// Watcher sagas
export function* watchCreateAgent() {
  yield takeLatest(createAgentRequest.type, createAgentSaga);
}

export function* watchUpdateAgent() {
  yield takeLatest(updateAgentRequest.type, updateAgentSaga);
}

export function* watchGetAgents() {
  yield takeLatest(getAgentsRequest.type, getAgentsSaga);
}

export function* watchGetAgentById() {
  yield takeLatest(getAgentByIdRequest.type, getAgentByIdSaga);
}

export function* watchDeleteAgent() {
  yield takeLatest(deleteAgentRequest.type, deleteAgentSaga);
}

export function* watchUpdateAgentStatus() {
  yield takeLatest(updateAgentStatusRequest.type, updateAgentStatusSaga);
}

// Root saga para agentes
export function* agentSaga() {
  yield takeLatest(createAgentRequest.type, createAgentSaga);
  yield takeLatest(updateAgentRequest.type, updateAgentSaga);
  yield takeLatest(getAgentsRequest.type, getAgentsSaga);
  yield takeLatest(getAgentByIdRequest.type, getAgentByIdSaga);
  yield takeLatest(deleteAgentRequest.type, deleteAgentSaga);
  yield takeLatest(updateAgentStatusRequest.type, updateAgentStatusSaga);
}
